import { Button, Checkbox, RadioButton } from "@newsela/angelou";
import {
  actionNames,
  actionPrefixes,
  objectTypes,
  withTrackEvent,
} from "@newsela/snitch";

import { useExportablesModalContext } from "@/components/AIComponents/ExportablesModal/ExportablesModalContext/useExportablesModalContext";
import {
  buttonCss,
  checkboxCss,
  imageCss,
  leftPanelCss,
  listCss,
  listItemCss,
  radioButtonCss,
} from "./ExportablesTemplateGallery.style";

import type { TemplateDefinition } from "@/components/AIComponents/ExportablesModal/templates/registry";

const ButtonWithTrackEvent = withTrackEvent(Button);

function ExportablesTemplateGallery() {
  const {
    handleTemplateSelection,
    isInAssignGraphicOrganizersExperiment,
    selectedExportable,
    setShowAssignmentWarning,
    setTemplatesToAssign,
    templateList,
    templatesToAssign,
  } = useExportablesModalContext();

  const getQaSelector = (exportable: TemplateDefinition<any>) => {
    const label = exportable.label || exportable.title;
    return `preview-${label.replace(/\s+/g, "-").toLowerCase()}-exportable`;
  };

  const renderExportableTemplate = (exportable: TemplateDefinition<any>) => {
    const { thumbnail, label, title, seti_identifier: identifier } = exportable;
    const isChecked = selectedExportable?.seti_identifier === identifier;
    const checkboxProps = {
      label: label || title,
      name: "graphic-organizer-templates",
      value: identifier,
    };

    const setSelectedTemplate = () => handleTemplateSelection(exportable);

    const handleCheckboxClick = (e: React.MouseEvent) => {
      const isChecked = (e.target as HTMLInputElement).checked;

      if (isChecked) {
        setTemplatesToAssign([...templatesToAssign, exportable]);
        setShowAssignmentWarning(false);
      } else {
        setTemplatesToAssign(
          templatesToAssign.filter(
            (t) => t.seti_identifier !== exportable.seti_identifier,
          ),
        );
      }
    };

    return (
      <li
        key={identifier}
        role={isInAssignGraphicOrganizersExperiment ? "listitem" : "none"}
      >
        <div css={listItemCss(isChecked)}>
          <ButtonWithTrackEvent
            dataProps={{
              "data-qa-selector": getQaSelector(exportable),
            }}
            actionName={actionNames.preview}
            actionPrefix={actionPrefixes.initiate}
            objectType={objectTypes.generatedTextAsset}
            __cssFor={{ root: buttonCss(isChecked) }}
            onClick={setSelectedTemplate}
          >
            {thumbnail && <img src={thumbnail} alt={title} css={imageCss} />}
          </ButtonWithTrackEvent>
          {isInAssignGraphicOrganizersExperiment ? (
            <Checkbox
              {...checkboxProps}
              defaultChecked={templatesToAssign.some(
                (t) => t.seti_identifier === exportable.seti_identifier,
              )}
              onClick={handleCheckboxClick}
              __cssFor={{ root: checkboxCss }}
            />
          ) : (
            <RadioButton
              {...checkboxProps}
              checked={isChecked}
              onClick={setSelectedTemplate}
              __cssFor={{ root: radioButtonCss }}
            />
          )}
        </div>
      </li>
    );
  };
  return (
    <div css={leftPanelCss}>
      <ul
        css={listCss}
        role={isInAssignGraphicOrganizersExperiment ? "list" : "radiogroup"}
        data-testid="exportables-template-group"
      >
        {templateList.map(renderExportableTemplate)}
      </ul>
    </div>
  );
}

export default ExportablesTemplateGallery;
